import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fxRatesApi, FxRate, FxRateKind, UpsertFxRateBody } from '@/lib/fxRates';
import { AppShell } from '@/components/app/AppShell';
import { CurrencyPicker } from '@/components/CurrencyPicker';

export function FxRatesPage() {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();

  const { data: rates = [], isLoading } = useQuery({
    queryKey: ['fx-rates', wsId],
    queryFn: () => fxRatesApi.list(wsId!),
    enabled: !!wsId,
  });

  const today = new Date().toISOString().slice(0, 10);
  const [filter, setFilter] = useState('');
  // Prefill the pair when arriving from a "missing rate" deep-link
  // (?base=USD&quote=JPY) on the Dashboard / Consolidation panel.
  const [searchParams] = useSearchParams();
  const [draft, setDraft] = useState<{ date: string; from: string; to: string; kind: FxRateKind; rate: string }>({
    date: today,
    from: (searchParams.get('base') || 'USD').toUpperCase().slice(0, 3),
    to: (searchParams.get('quote') || 'JPY').toUpperCase().slice(0, 3),
    kind: 'period_end',
    rate: '',
  });

  const upsert = useMutation({
    mutationFn: () => fxRatesApi.upsert(wsId!, {
      date: draft.date,
      fromCurrency: draft.from.toUpperCase(),
      toCurrency: draft.to.toUpperCase(),
      kind: draft.kind,
      rate: Number(draft.rate),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-rates', wsId] });
      setDraft((d) => ({ ...d, rate: '' }));
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => fxRatesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fx-rates', wsId] }),
  });

  const fetchLive = useMutation({
    mutationFn: () => fxRatesApi.fetchLive(wsId!, {
      fromCurrency: draft.from.toUpperCase(),
      toCurrency: draft.to.toUpperCase(),
      kind: draft.kind,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['fx-rates', wsId] });
      setDraft((d) => ({ ...d, rate: String(r.rate), date: r.date }));
    },
  });

  const importCsv = useMutation({
    mutationFn: async (text: string) => {
      const rows = parseFxCsv(text, today);
      if (rows.length === 0) throw new Error('No valid rows found. Expected: date,from,to,kind,rate');
      let ok = 0;
      for (const row of rows) { await fxRatesApi.upsert(wsId!, row); ok++; }
      return { ok };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fx-rates', wsId] }),
  });

  return (
    <AppShell title={t('fx.title')} subtitle={t('fx.subtitle')}>
      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('fx.title')}</h1>
          <p className="mt-1 text-muted-foreground max-w-2xl">{t('fx.subtitle')}</p>
        </div>

        {/* Add-rate row */}
        <div className="rounded-md border border-border bg-card p-4">
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('fx.date')}</span>
              <input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('fx.from')}</span>
              <CurrencyPicker
                value={draft.from}
                onChange={(v) => setDraft((d) => ({ ...d, from: v }))}
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('fx.to')}</span>
              <CurrencyPicker
                value={draft.to}
                onChange={(v) => setDraft((d) => ({ ...d, to: v }))}
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('fx.kind')}</span>
              <select
                value={draft.kind}
                onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value as FxRateKind }))}
                className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs"
              >
                <option value="period_end">{t('fx.kind_end')}</option>
                <option value="avg">{t('fx.kind_avg')}</option>
                <option value="historical">{t('fx.kind_historical')}</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('fx.rate')}</span>
              <input
                type="text"
                inputMode="decimal"
                value={draft.rate}
                onChange={(e) => setDraft((d) => ({ ...d, rate: e.target.value }))}
                placeholder="0.0067"
                className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs"
              />
            </label>
            <button
              onClick={() => upsert.mutate()}
              disabled={upsert.isPending || !draft.rate.trim() || draft.from.length !== 3 || draft.to.length !== 3}
              className="h-[28px] rounded-md bg-foreground text-background text-[11px] font-semibold hover:bg-foreground/90 disabled:opacity-60"
            >
              {t('fx.add')}
            </button>
            <button
              onClick={() => fetchLive.mutate()}
              disabled={fetchLive.isPending || draft.from.length !== 3 || draft.to.length !== 3}
              title={t('fx.fetch_live_hint', { defaultValue: 'Pull the latest ECB spot rate (Frankfurter)' })}
              className="h-[28px] rounded-md border border-border text-[11px] font-semibold hover:bg-foreground/[0.04] disabled:opacity-60 px-2"
            >
              {fetchLive.isPending ? t('common.loading') : t('fx.fetch_live', { defaultValue: 'Fetch live' })}
            </button>
          </div>
          {(upsert.error || fetchLive.error) && (
            <p className="mt-2 text-xs text-rose-700">{((upsert.error || fetchLive.error) as Error).message}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!isLoading && rates.length > 0 && (
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value.toUpperCase())}
              placeholder={t('fx.filter', { defaultValue: 'Filter by currency (e.g. USD)…' })}
              className="num w-full sm:w-64 rounded-md border border-border bg-white px-3 py-2 text-sm"
            />
          )}
          <label className="text-[11px] font-semibold px-3 py-2 rounded-md border border-border hover:bg-foreground/[0.04] cursor-pointer">
            {importCsv.isPending ? t('common.loading') : t('fx.import_csv', { defaultValue: 'Import CSV' })}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) importCsv.mutate(await f.text());
                e.target.value = '';
              }}
            />
          </label>
          <span className="text-[10px] text-muted-foreground">{t('fx.import_hint', { defaultValue: 'date,from,to,kind,rate' })}</span>
        </div>
        {(importCsv.error || importCsv.isSuccess) && (
          <p className={`text-xs ${importCsv.error ? 'text-rose-700' : 'text-emerald-600'}`}>
            {importCsv.error ? (importCsv.error as Error).message : t('fx.import_done', { defaultValue: `Imported ${importCsv.data?.ok ?? 0} rates.`, count: importCsv.data?.ok ?? 0 })}
          </p>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : rates.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card p-12 text-center">
            <p className="text-foreground font-medium">{t('fx.empty')}</p>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border bg-foreground/[0.02]">
                  <th className="py-2 px-4 text-[10px] uppercase tracking-[0.16em] font-medium">{t('fx.date')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium">{t('fx.from')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium">{t('fx.to')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium">{t('fx.kind')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium text-right">{t('fx.rate')}</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rates.filter((r) => !filter || r.fromCurrency.includes(filter) || r.toCurrency.includes(filter)).map((r) => (
                  <RateRow
                    key={r.id}
                    r={r}
                    onDelete={() => del.mutate(r.id)}
                    // upsert is the form's parameterless mutation (closes
                    // over `draft`); for in-row rate edits we hit the API
                    // directly with the row's identity + new rate, then
                    // invalidate the cache so the table re-renders.
                    onSaveRate={async (newRate) => {
                      await fxRatesApi.upsert(wsId!, {
                        date: r.date,
                        fromCurrency: r.fromCurrency,
                        toCurrency: r.toCurrency,
                        kind: r.kind,
                        rate: newRate,
                      });
                      qc.invalidateQueries({ queryKey: ['fx-rates', wsId] });
                    }}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </AppShell>
  );
}

function RateRow({
  r, onDelete, onSaveRate, t,
}: {
  r: FxRate;
  onDelete: () => void;
  /**
   * Only the rate value is editable — date/pair/kind compose the row's
   * unique key in the backend (see migration 011 unique index). Changing
   * any of those would mean a *different* row, not an edit of this one.
   * For that, the user adds a new row and deletes the old.
   */
  onSaveRate: (newRate: number) => Promise<unknown>;
  t: (k: string) => string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(String(r.rate));
  const [saving, setSaving] = useState(false);

  async function save() {
    const next = Number(draft);
    if (!Number.isFinite(next) || next <= 0) { setEditing(false); return; }
    if (next === r.rate) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSaveRate(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr className="hover:bg-foreground/[0.02]">
      <td className="num py-2 px-4 text-foreground/90">{r.date}</td>
      <td className="num py-2 px-3 text-foreground/90">{r.fromCurrency}</td>
      <td className="num py-2 px-3 text-foreground/90">{r.toCurrency}</td>
      <td className="py-2 px-3 text-muted-foreground">{t(`fx.kind_${r.kind === 'period_end' ? 'end' : r.kind}`)}</td>
      <td className="num py-2 px-3 text-right font-medium text-foreground tabular-nums">
        {editing ? (
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save();
              if (e.key === 'Escape') { setDraft(String(r.rate)); setEditing(false); }
            }}
            autoFocus
            inputMode="decimal"
            className="num w-28 rounded-md border border-border bg-white px-2 py-1 text-xs text-right"
          />
        ) : (
          r.rate.toLocaleString(undefined, { maximumFractionDigits: 8 })
        )}
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        {editing ? (
          <>
            <button onClick={() => { setDraft(String(r.rate)); setEditing(false); }} disabled={saving} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1">
              {t('common.cancel')}
            </button>
            <button onClick={save} disabled={saving} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 px-2 py-1 disabled:opacity-60">
              {saving ? t('settings.saving') : t('common.save')}
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1">
              {t('common.edit')}
            </button>
            <button onClick={onDelete} className="text-[11px] text-muted-foreground hover:text-rose-700 px-2 py-1">
              {t('workbook.delete')}
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

const FX_KINDS: FxRateKind[] = ['avg', 'period_end', 'historical'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a pasted/uploaded CSV of FX rates. Accepts, per line:
 *   date,from,to,kind,rate   (5 cols)
 *   from,to,kind,rate        (4 cols — date defaults to today)
 *   from,to,rate             (3 cols — date=today, kind=period_end)
 * A header row (first cell "date"/"from") is skipped. Invalid rows are dropped.
 */
function parseFxCsv(text: string, today: string): UpsertFxRateBody[] {
  const out: UpsertFxRateBody[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const c = line.split(',').map((s) => s.trim());
    if (/^(date|from|fromcurrency)$/i.test(c[0])) continue; // header
    let date = today, from = '', to = '', kind: FxRateKind = 'period_end', rateStr = '';
    if (c.length >= 5) { [date, from, to, , rateStr] = c; kind = (FX_KINDS.includes(c[3] as FxRateKind) ? c[3] : 'period_end') as FxRateKind; }
    else if (c.length === 4) { [from, to, , rateStr] = c; kind = (FX_KINDS.includes(c[2] as FxRateKind) ? c[2] : 'period_end') as FxRateKind; }
    else if (c.length === 3) { [from, to, rateStr] = c; }
    else continue;
    from = from.toUpperCase(); to = to.toUpperCase();
    const rate = Number(rateStr);
    if (!/^[A-Z]{3}$/.test(from) || !/^[A-Z]{3}$/.test(to) || from === to) continue;
    if (!Number.isFinite(rate) || rate <= 0) continue;
    if (!ISO_DATE.test(date)) date = today;
    out.push({ date, fromCurrency: from, toCurrency: to, kind, rate });
  }
  return out;
}
